import React, { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import useIsDesktop from '../../hooks/useIsDesktop';

const STORAGE_KEY = 'rental-ace-theme';

function getInitialTheme() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function Navbar() {
  const [theme, setTheme] = useState(getInitialTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // collapse the mobile menu automatically if the viewport grows past the
  // breakpoint while it's open (e.g. rotating a tablet)
  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
  }, [isDesktop]);

  function toggleTheme() {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }

  const ThemeIcon = theme === 'dark' ? Sun : Moon;
  const themeLabel = theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <header style={styles.nav}>
      <div style={styles.inner}>
        <a href="/" style={styles.brand}>
          <img src="/logo-mark.png" alt="" style={styles.logo} />
          <span style={styles.wordmark}>Ace Apartments</span>
        </a>

        {isDesktop ? (
          <nav style={styles.linksDesktop} aria-label="Primary">
            <a href="/login" style={styles.loginLink} className="link-underline">Login</a>
            <a href="/register" style={styles.registerBtn} className="btn-lift">Register</a>
            <button
              onClick={toggleTheme}
              style={styles.iconBtn}
              aria-label={themeLabel}
              title={themeLabel}
            >
              <ThemeIcon size={18} />
            </button>
          </nav>
        ) : (
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={styles.hamburger}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        )}
      </div>

      {!isDesktop && menuOpen && (
        <nav style={styles.mobileMenu} aria-label="Primary">
          <a href="/login" style={styles.mobileLink} onClick={() => setMenuOpen(false)}>Login</a>
          <a href="/register" style={styles.mobileRegisterBtn} onClick={() => setMenuOpen(false)}>Register</a>
          <button onClick={toggleTheme} style={styles.mobileThemeBtn}>
            <ThemeIcon size={16} />
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
        </nav>
      )}
    </header>
  );
}

const styles = {
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
  },
  inner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
  },
  logo: { width: '28px', height: '28px', objectFit: 'contain' },
  wordmark: {
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    fontSize: '16px',
    color: 'var(--color-ink)',
  },
  linksDesktop: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  loginLink: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--color-ink-soft)',
  },
  registerBtn: {
    padding: '9px 18px',
    borderRadius: '10px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    fontSize: '14px',
    fontWeight: 700,
    textDecoration: 'none',
  },
  iconBtn: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-border)',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    color: 'var(--color-ink-soft)',
    cursor: 'pointer',
  },
  hamburger: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-ink)',
    cursor: 'pointer',
  },
  mobileMenu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px 24px 24px',
    borderTop: '1px solid var(--color-border)',
  },
  mobileLink: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--color-ink-soft)',
    textDecoration: 'none',
    padding: '6px 0',
  },
  mobileRegisterBtn: {
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text-on-brand)',
    fontSize: '15px',
    fontWeight: 700,
    textDecoration: 'none',
    textAlign: 'center',
  },
  mobileThemeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 0',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-ink-soft)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default Navbar;
